import { Dispatch } from 'react';

import axios from 'axios';
import { isUndefined } from 'lodash';
import { TFunction } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { SaveCredentialResponse } from 'admin/credentials/Credentials';
import { SourceId } from 'admin/sources/Sources';
import { AddCredentials } from 'common/credentials/add/AddCredentials';
import { MessageAction, MessageActionType } from 'common/message-bar/MessageBar';
import { Props as EditorProps } from 'common/response-editor/ResponseEditor';
import { VobMetadata } from 'common/response-editor/vob/VobMetadata.types';
import { isVobEnabled } from 'common/response-editor/vob/VobUtils';
import { VariableType } from 'common/variables/Variables';
import { RepliesAction, RepliesActionType } from 'reviews2/feed/review-yeti/Replies.store';
import { CustomTemplateLabel } from 'reviews2/feed/review-yeti/reply/editor/custom-elements/CustomTemplateLabel';
import { Lock } from 'reviews2/feed/review-yeti/reply/editor/ReplyEditor.types';
import { convertToYetiResponse } from 'reviews2/feed/review-yeti/reply/ReviewReply.util';
import { ReviewActionType, ReviewAction } from 'reviews2/feed/review-yeti/Review.store';
import { ReviewProps, ReplyTo, ReplyMode, ReadOnlyReason } from 'reviews2/feed/review-yeti/Review.types';
import { Sentiment, YetiReview } from 'reviews2/feed/Reviews.types';
import { getReview3, publish } from 'reviews2/Reviews.api';

export interface EditorArgs {
    context: ReviewProps;
    replyTo?: ReplyTo;
    editReply?: YetiReview;
    editorLockRef: React.MutableRefObject<Lock | null>;
    editorRef: React.MutableRefObject<any>;
    dispatchMessage: React.Dispatch<MessageAction>;
    currentReply?: YetiReview;
    replyMode?: ReplyMode;
    dispatch?: Dispatch<RepliesAction>;
    categoryIDs: string[];
    canPublish?: boolean;
    translate: TFunction;
    canEdit?: boolean;
    focusHandler?: () => void;
    blurHandler?: () => void;
    dispatchReview?: Dispatch<ReviewAction>;
}

const REVIEW_SENTIMENT_TO_COMMON: Record<number, number> = {
    [Sentiment.Positive]: 1,
    [Sentiment.Negative]: 2,
    [Sentiment.Neutral]: 3,
    [Sentiment.None]: 4,
};

export const createEditorProps = ({
    context,
    replyTo,
    dispatch,
    dispatchMessage,
    replyMode,
    editReply,
    currentReply,
    categoryIDs,
    canPublish,
    translate,
    canEdit,
    focusHandler,
    blurHandler,
    dispatchReview,
}: EditorArgs): EditorProps => {
    const { review, profile, handlers } = context;
    const { editorOptions, readOnly, readOnlyReason } = context.options!;
    const { locale } = profile?.user!;
    const attributeMap: Record<string, string> = {};
    const vobReviewResponseEnabled = editorOptions?.enableVOBResponding && isVobEnabled(review.tenant, profile);

    review.location_attributes?.forEach((attribute: Record<string, string>) => {
        attributeMap[attribute.reputation_name] = attribute.attribute_value;
    });

    return {
        id: `${review.document_id.replace(/\W/g, '')}`,
        agencyId: review!.r4e_agency_id,
        tenantId: review!.r4e_tenant_id,
        userLocale: locale,
        focus: false,
        placeholder: editorOptions?.placeholder,
        widgetPlacement: editorOptions?.widgetPlacement,
        textOptions: {
            defaultText: editorOptions?.defaultText
                ? [{ text: editorOptions?.defaultText || '', tags: [], attributes: [] }]
                : undefined,
            displayCharCount: editorOptions?.showCharCount,
        },
        toolBarOptions: {
            enable: true,
            specialOptions: {
                emojiOptions: {
                    enable: editorOptions?.enableEmojis,
                },
                templateOptions: {
                    enable: editorOptions?.enableTemplates,
                    expandTemplateRequest: {
                        moduleGroup: editorOptions?.moduleGroups,
                        requiredLevels: editorOptions?.templateLevels,
                        variableTypeAndObjectIDs: [
                            {
                                variableType: VariableType.REVIEW_HOLISTIC,
                                objectID: review!.document_id,
                            },
                        ],
                        locationAttributes: {
                            location: review!.location?.code,
                            ...attributeMap,
                        },
                        ...(editorOptions?.filterTemplates
                            ? {
                                  sentiment: REVIEW_SENTIMENT_TO_COMMON[review.taxonomy_overall_sentiment],
                                  sourceID: review!.review_source_id,
                                  roleIDs: profile?.user?.roleIDs,
                                  categoryIDs: categoryIDs.length ? categoryIDs : undefined,
                              }
                            : {}),
                    },
                    customLabelElement: CustomTemplateLabel,
                },
                mediaOptions: {
                    enable: editorOptions?.enableMedia && review!.review_source_id === SourceId.Facebook,
                    accept: 'image/*, video/*',
                },
                responseGeneratorOptions: !vobReviewResponseEnabled
                    ? { ...editorOptions?.responseGeneratorOptions }
                    : undefined,
            },
            respondButtonOptions: {
                enable: editorOptions?.respondButtonOptions?.enable,
                type: editorOptions?.respondButtonOptions?.buttonSplitOptions?.length ? 2 : 1,
                buttonSplitOptions: editorOptions?.respondButtonOptions?.buttonSplitOptions,
                menuAlignment: editorOptions?.respondButtonOptions?.menuAlignment,
                // eslint-disable-next-line
                text: editorOptions?.buttonText
                    ? editorOptions.buttonText
                    : // eslint-disable-next-line
                    isUndefined(canPublish)
                    ? translate('RESPOND')
                    : canPublish === true
                    ? translate('REPLY_PUBLISH')
                    : translate('REPLY_SUBMIT'),
            },
            cancelButtonOptions: {
                enable: editorOptions?.enableCancel || [ReplyMode.Edit, ReplyMode.Recommend].includes(replyMode!),
                text: editorOptions?.cancelText,
            },
            generateAIResponseOnLoad: editorOptions?.toolBarOptions?.generateAIResponseOnLoad,
        },
        canEdit,
        handlers: {
            onFocus: focusHandler,
            onBlur: blurHandler,
            onResponseSubmit: async (response: any, metadata?: VobMetadata, evaluationResponse?: any) => {
                const publishReq = convertToYetiResponse({
                    context,
                    currentReply,
                    editReply,
                    replyTo,
                    replyMode,
                    response,
                });

                try {
                    if (evaluationResponse) {
                        try {
                            axios.post(`/api/tenants/${review.r4e_tenant_id}/nuvi/vob/response-cache`, {
                                args: {
                                    reviewId: review.document_id,
                                    locationId: review.r4e_location_uid,
                                    evaluation: evaluationResponse,
                                },
                            });
                        } catch (error) {
                            console.log(error);
                        }
                    }

                    const result = await trackPromise(
                        publish(review.r4e_tenant_id, publishReq, { agencyId: review.r4e_agency_id }),
                        `review-replies-${review!.document_id}`,
                    );
                    const { review: updatedReview } = await trackPromise(
                        getReview3(review.r4e_tenant_id, review.document_id, { agencyId: review.r4e_agency_id }),
                        `review-replies-${review!.document_id}`,
                    );

                    dispatchReview?.({
                        type: ReviewActionType.UpdateReview,
                        review: {
                            respondable_type: updatedReview.respondable_type,
                        },
                    });

                    if (ReplyMode.Edit === replyMode) {
                        dispatch?.({
                            type: RepliesActionType.UpdateReply,
                            reply: result,
                        });
                        dispatch?.({
                            type: RepliesActionType.SetReplyMode,
                            replyMode: ReplyMode.Add,
                        });
                    } else {
                        dispatch?.({
                            type: RepliesActionType.AddReply,
                            reply: result,
                        });
                        dispatch?.({
                            type: RepliesActionType.AddNewReply,
                            reply: result,
                        });
                    }

                    if (metadata && metadata.vob_score_object) {
                        result.vob_threshold_met = metadata.vob_score_object.meets_threshold;
                    }

                    // once the result is available, send the response to the actions handler
                    if (result && response?.options?.respondButtonSplitOption) {
                        editorOptions?.actionsRespondButtonHandler?.(response, result);
                    }

                    if (metadata) {
                        axios.post(`/api/tenants/${review.r4e_tenant_id}/nuvi/vob/metadata`, {
                            args: {
                                responseId: result.document_id,
                                reviewId: review.document_id,
                                metadata,
                            },
                        });
                    }

                    handlers?.onSuccessfulResponse?.({ review: updatedReview });
                } catch ({ response }: any) {
                    const error = response?.data?.error?.detail?.status?.errors?.[0];
                    const message = error?.message;
                    const code = error?.code;

                    if (code === 'error.contains.flagged.word') {
                        dispatchMessage({
                            type: MessageActionType.ShowError,
                            text: message,
                        });
                    }
                }
            },
            onCancelClick: () => {
                dispatch?.({
                    type: RepliesActionType.SetEditReply,
                });
                dispatch?.({
                    type: RepliesActionType.SetReplyMode,
                    replyMode: ReplyMode.Add,
                });
            },
        },
        readOnlyOptions: {
            enable: readOnly!,
            text: editorOptions?.readOnlyText,
            CustomElement: readOnlyReason === ReadOnlyReason.Credentials ? AddCredentials : undefined,
            customElementProps:
                readOnlyReason === ReadOnlyReason.Credentials
                    ? {
                          agencyId: review!.r4e_agency_id,
                          tenantId: review!.r4e_tenant_id,
                          location: review!.location,
                          source: review!.source,
                          allowAddCredentials: editorOptions?.allowAddCredentials,
                          onCredentialSave: ({ credential }: SaveCredentialResponse) => {
                              context.handlers?.onSaveCredential?.(review!, credential);
                          },
                          onCredentialFail: (error: any) => {
                              context.handlers?.onFailedSaveCredential?.(error);
                          },
                      }
                    : undefined,
        },
        vobReviewResponseEnabled,
        generateAIResponseOnLoad: editorOptions?.generateAIResponseOnLoad,
    };
};
